import { ContactSelectionLight } from '@/components/contact-selection/contact-selection.light';
import { ContactSelectionWoo } from '@/components/contact-selection/contact-selection.woo';
import { ContactSelectionWooDark } from '@/components/contact-selection/contact-selection.woo-dark';
import type { ContactSelectionProps, ContactType } from '@/components/contact-selection/props';
import { ErrorSidePanel } from '@/components/errors';
import { useGetPersonalInfo } from '@/hooks/api';
import { useSendCode } from '@/hooks/api/contact-validation.hooks';
import { templates, type SkinPageConfig } from '@nosinovacao/nosid-mfe-common';
import { createFileRoute } from '@tanstack/react-router';
import type { ProfileCodeInputState } from '../-model/flow';

export const Route = createFileRoute(
  '/(tv)/tv/$id/(tv-profiles)/_sheet/profiles/$profileId/update/contact-selection/',
)({
  component: RouteComponent,
  errorComponent: ErrorSidePanel,
});

function RouteComponent() {
  const { id, profileId } = Route.useParams();
  const navigate = Route.useNavigate();

  const { sendCodeAsync, isSending, captcha } = useSendCode();
  const { data: personalInfo } = useGetPersonalInfo();

  const phoneAlias = personalInfo?.UsernameAliases?.find((a) => a.Type === 'Mobile')?.Username;
  const emailAlias = personalInfo?.UsernameAliases?.find((a) => a.Type === 'Email')?.Username;

  const handleContactSelected = async (contactType: ContactType, targetId: string) => {
    if (captcha.challenge && !captcha.token) return;
    await sendCodeAsync({ targetId, type: contactType, captcha });
    void navigate({
      to: '/tv/$id/profiles/$profileId/update/code-input',
      params: { id, profileId },
      state: { contactType, targetId } as ProfileCodeInputState,
    });
  };

  const props: ContactSelectionProps = {
    translations: {
      phone: 'Common.ContactValidation.ContactSelection.Phone',
      email: 'Common.ContactValidation.ContactSelection.Email',
    },
    phoneAlias,
    emailAlias,
    onContactSelected: (ct, tid) => void handleContactSelected(ct, tid),
    isSending,
    captchaChallenge: captcha.challenge,
    onCaptchaChange: captcha.setToken,
  };

  return templates.getSkinSelector(skinConfig(props));
}

function skinConfig(props: ContactSelectionProps): SkinPageConfig {
  return {
    skins: [
      {
        selector: 'isWooTvTemplate',
        component: <ContactSelectionWooDark {...props} />,
      },
      {
        selector: 'isWooTemplate',
        component: <ContactSelectionWoo {...props} />,
      },
    ],
    defaultValue: <ContactSelectionLight {...props} />,
  };
}
